import { useTranslation } from 'react-i18next';
import { useAccountFlow, useCurrencySymbol } from '@shared/hooks';
import type { AccountByIdResponse } from '@shared/api';
import { SidebarItem, SidebarRow } from '@widgets';
import { Button, Icon, Text } from '@shared/widgets';
import SettingIcon from '@assets/icons/setting.svg';
import NotificationIcon from '@assets/icons/notification.svg';

interface AccountSidebarItemsProps {
  account: AccountByIdResponse;
  onShowEditModal: () => void;
  onShowChangeAlertModal: () => void;
  onShowEditRecipientsModal: () => void;
}

export const AccountSidebarItems = (props: AccountSidebarItemsProps) => {
  const { t } = useTranslation('account');

  const accountFlow = useAccountFlow(props.account.id);
  const symbol = useCurrencySymbol(props.account?.currency);
  return (
    <>
      <SidebarItem title={t('sidebar.accountDetails.title')} icon={<Icon icon={<SettingIcon />} color='inherit' />}>
        <SidebarRow label={t('sidebar.accountDetails.fields.accountOwner')} value={props.account.name || t('sidebar.accountDetails.fields.none')} />
        <SidebarRow
          label={t('sidebar.accountDetails.fields.accountLabel')}
          value={props.account.accountLabel || t('sidebar.accountDetails.fields.none')}
        />
        <SidebarRow
          label={t('sidebar.accountDetails.fields.costCentre')}
          value={props.account.costCentre || t('sidebar.accountDetails.fields.none')}
        />
        <SidebarRow
          label={t('sidebar.accountDetails.fields.accountCategory')}
          value={props.account.category || t('sidebar.accountDetails.fields.none')}
        />
        {(accountFlow?.editAccountCategory || accountFlow?.editAccountLabel || accountFlow?.editCostCentre) && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.onShowEditModal}>
            {t('buttons.editDetails', { ns: 'common' })}
          </Button>
        )}
      </SidebarItem>
      <SidebarItem
        title={t('sidebar.alerts.title')}
        icon={<Icon icon={<NotificationIcon />} color='inherit' />}
        isHidden={!(accountFlow?.accountAlertsSettings.minimalBalance || accountFlow?.accountAlertsSettings.declinedAuths)}
      >
        <SidebarRow
          label={t('sidebar.alerts.fields.minimalAvailableBalance')}
          isHidden={!accountFlow?.accountAlertsSettings.minimalBalance}
          value={
            props.account.minimalBalanceAlertSubscription?.minimalBalance
              ? `${symbol}${props.account.minimalBalanceAlertSubscription?.minimalBalance}`
              : t('sidebar.alerts.fields.none')
          }
        />
        {props.account.minimalBalanceAlertSubscription?.recipientDetails?.map((item, idx) => (
          <SidebarRow
            key={idx}
            isHidden={
              !(
                accountFlow?.accountAlertsSettings.minimalBalance &&
                !!props.account?.minimalBalanceAlertSubscription?.recipientDetails?.some((row) => row.email != null || row.firstName != null)
              )
            }
            label={item.firstName}
            value={item.email}
          />
        ))}
        {accountFlow?.accountAlertsSettings.minimalBalance && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.onShowChangeAlertModal}>
            {t('sidebar.alerts.buttons.changeAlert')}
          </Button>
        )}
        <Text className='mb-5' size='sm'>
          {t('sidebar.alerts.buttons.declinedAuthorizations')}
        </Text>
        {props.account.declinedAuthorizationSubscription?.recipientDetails?.map((item, idx) => (
          <SidebarRow key={idx} isHidden={!accountFlow?.accountAlertsSettings.minimalBalance} label={item.firstName} value={item.email} />
        ))}
        {accountFlow?.accountAlertsSettings.declinedAuths && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.onShowEditRecipientsModal}>
            {t('sidebar.alerts.buttons.editRecipients')}
          </Button>
        )}
      </SidebarItem>
    </>
  );
};
